import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Folder, FolderPlus, FilePlus, Trash2, Download, Loader2, Plus, Edit3, Check, X, ChevronRight, ChevronDown, FileText, History } from 'lucide-react';
import SARButton from './SARButton';
import ProjectTimeline from './ProjectTimeline';

const TYPE_COLORS = {
  negociacion:   'bg-blue-500/15 text-blue-300 border-blue-500/30',
  contrato:      'bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/30',
  tecnica:       'bg-purple-500/15 text-purple-300 border-purple-500/30',
  permisos:      'bg-orange-500/15 text-orange-300 border-orange-500/30',
  mantenimiento: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  auditoria:     'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
  otros:         'bg-white/10 text-white/70 border-white/20',
};

const humanSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SiteProjectsPanel = ({ siteId, siteInfo }) => {
  const { user } = useAuth();
  const canWrite = user && (user.role === 'admin' || user.role === 'gestor');
  // siteInfo optional: {lat, lng, address, name, code, height_m, afecciones_report}
  // used to render an inline "Generar SAR" button inside each expanded project.

  const [types, setTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [tree, setTree] = useState(null); // {project, folders, documents}
  const [treeLoading, setTreeLoading] = useState(false);

  // Create-project form
  const [showCreate, setShowCreate] = useState(false);
  const [newType, setNewType] = useState('otros');
  const [newName, setNewName] = useState('');

  // Rename project
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Folder create
  const [creatingFolderIn, setCreatingFolderIn] = useState(null); // folder_id parent or 'root'
  const [newFolderName, setNewFolderName] = useState('');

  // Upload
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null); // {projectId, folderId}

  // Timeline visibility per project
  const [timelineOpen, setTimelineOpen] = useState({}); // projectId -> bool

  const loadProjects = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/site-projects/site/${siteId}`);
      setProjects(r.data.items || []);
    } catch (e) {
      toast.error('Error cargando proyectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!siteId) return;
    api.get('/site-projects/types').then((r) => setTypes(r.data || []));
    loadProjects();
  }, [siteId]);

  const openProject = async (id) => {
    if (openProjectId === id) {
      setOpenProjectId(null);
      setTree(null);
      return;
    }
    setOpenProjectId(id);
    setTreeLoading(true);
    try {
      const r = await api.get(`/site-projects/${id}/tree`);
      setTree(r.data);
    } catch (e) {
      toast.error('Error cargando el proyecto');
    } finally {
      setTreeLoading(false);
    }
  };

  const reloadTree = async (id) => {
    const r = await api.get(`/site-projects/${id}/tree`);
    setTree(r.data);
  };

  const createProject = async () => {
    if (!newName.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      await api.post('/site-projects/', {
        site_id: siteId, type: newType, name: newName.trim(), description: '',
      });
      toast.success('Proyecto creado');
      setNewName(''); setNewType('otros'); setShowCreate(false);
      loadProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando proyecto'); }
  };

  const renameProject = async (id) => {
    if (!renameValue.trim()) return;
    try {
      await api.patch(`/site-projects/${id}`, { name: renameValue.trim() });
      setRenamingId(null);
      loadProjects();
      if (openProjectId === id && tree) setTree({ ...tree, project: { ...tree.project, name: renameValue.trim() } });
      toast.success('Nombre actualizado');
    } catch (e) { toast.error('Error renombrando'); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('¿Eliminar el proyecto y todas sus carpetas/documentos?')) return;
    try {
      await api.delete(`/site-projects/${id}`);
      toast.success('Proyecto eliminado');
      if (openProjectId === id) { setOpenProjectId(null); setTree(null); }
      loadProjects();
    } catch (e) { toast.error('Error eliminando'); }
  };

  const createFolder = async (projectId, parentId) => {
    if (!newFolderName.trim()) return;
    try {
      await api.post(`/site-projects/${projectId}/folders`, { name: newFolderName.trim(), parent_id: parentId });
      setNewFolderName(''); setCreatingFolderIn(null);
      reloadTree(projectId);
      toast.success('Carpeta creada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando carpeta'); }
  };

  const deleteFolder = async (projectId, folderId) => {
    if (!window.confirm('¿Eliminar carpeta y su contenido?')) return;
    try {
      await api.delete(`/site-projects/folders/${folderId}`);
      reloadTree(projectId);
      toast.success('Carpeta eliminada');
    } catch (e) { toast.error('Error eliminando'); }
  };

  const triggerUpload = (projectId, folderId = null) => {
    setUploadTarget({ projectId, folderId });
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadTarget) return;
    const fd = new FormData();
    fd.append('file', file);
    if (uploadTarget.folderId) fd.append('folder_id', uploadTarget.folderId);
    const toastId = toast.loading(`Subiendo ${file.name}…`);
    try {
      await api.post(`/site-projects/${uploadTarget.projectId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documento subido', { id: toastId });
      reloadTree(uploadTarget.projectId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error subiendo', { id: toastId });
    } finally { setUploadTarget(null); }
  };

  const deleteDoc = async (projectId, docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await api.delete(`/site-projects/documents/${docId}`);
      reloadTree(projectId);
      toast.success('Documento eliminado');
    } catch (e) { toast.error('Error eliminando'); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/site-projects/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.name || 'documento';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error descargando'); }
  };

  if (!siteId) return null;

  return (
    <div className="mt-4" data-testid="site-projects-panel">
      <input ref={fileInputRef} type="file" hidden onChange={handleFileSelected}/>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Folder size={14} className="text-[#A3FF12]"/>
          <h4 className="text-sm font-black uppercase tracking-wide text-white/60">Proyectos del site</h4>
          {projects.length > 0 && <span className="text-[10px] font-bold text-white/40 bg-white/5 px-1.5 py-0.5 rounded-full">{projects.length}</span>}
        </div>
        {canWrite && (
          <button onClick={() => setShowCreate(!showCreate)}
            className="text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1 bg-[#A3FF12]/10 hover:bg-[#A3FF12]/20 border border-[#A3FF12]/30 text-[#A3FF12] px-2.5 py-1 rounded-full"
            data-testid="btn-new-project">
            <Plus size={12}/> Nuevo
          </button>
        )}
      </div>

      {showCreate && canWrite && (
        <div className="bg-black/40 border border-[#A3FF12]/30 rounded-xl p-3 mb-3 space-y-2" data-testid="new-project-form">
          <div className="flex gap-2 flex-wrap">
            {types.map((t) => (
              <button key={t.id} onClick={() => setNewType(t.id)}
                className={`text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full border ${
                  newType === t.id ? TYPE_COLORS[t.id] : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre del proyecto (ej. Contrato Vodafone 2026)"
              onKeyDown={(e) => e.key === 'Enter' && createProject()}
              className="flex-1 bg-black border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-[#A3FF12]/50 outline-none"/>
            <button onClick={createProject} className="bg-[#A3FF12] text-black px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-[#b8ff3a]">Crear</button>
            <button onClick={() => setShowCreate(false)} className="text-white/40 hover:text-white p-1.5"><X size={16}/></button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="animate-spin text-[#A3FF12]" size={20}/></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-6 text-white/40 text-xs">
          Aún no hay proyectos en este site. {canWrite && 'Crea el primero arriba.'}
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map((p) => (
            <div key={p.id} className="bg-black/30 border border-white/10 rounded-xl overflow-hidden" data-testid={`project-${p.id}`}>
              <div className="flex items-center gap-2 p-2.5">
                <button onClick={() => openProject(p.id)} className="text-white/50 hover:text-white">
                  {openProjectId === p.id ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                </button>
                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${TYPE_COLORS[p.type] || TYPE_COLORS.otros}`}>
                  {p.type_label}
                </span>
                {renamingId === p.id ? (
                  <div className="flex-1 flex gap-1">
                    <input value={renameValue} autoFocus onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && renameProject(p.id)}
                      className="flex-1 bg-black border border-[#A3FF12]/40 rounded px-2 py-0.5 text-sm text-white outline-none"/>
                    <button onClick={() => renameProject(p.id)} className="text-[#A3FF12]"><Check size={14}/></button>
                    <button onClick={() => setRenamingId(null)} className="text-white/40"><X size={14}/></button>
                  </div>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-white font-bold truncate">{p.name}</span>
                    <span className="text-[10px] text-white/40 flex-shrink-0">{p.folder_count} 📁 · {p.document_count} 📄</span>
                  </>
                )}
                {canWrite && renamingId !== p.id && (
                  <>
                    <button onClick={() => { setRenamingId(p.id); setRenameValue(p.name); }}
                      className="text-white/40 hover:text-[#A3FF12] p-1" title="Renombrar"><Edit3 size={13}/></button>
                    <button onClick={() => deleteProject(p.id)} className="text-white/40 hover:text-red-400 p-1" title="Eliminar"><Trash2 size={13}/></button>
                  </>
                )}
              </div>

              {openProjectId === p.id && (
                <div className="border-t border-white/10 bg-black/40 p-2.5 space-y-2">
                  {treeLoading ? (
                    <div className="flex items-center justify-center py-3"><Loader2 className="animate-spin text-[#A3FF12]" size={16}/></div>
                  ) : tree ? (
                    <>
                      {/* SAR quick-action — visible en TODOS los proyectos */}
                      {siteInfo?.lat && siteInfo?.lng && (
                        <div className="bg-gradient-to-r from-[#A3FF12]/10 to-transparent border border-[#A3FF12]/25 rounded-lg p-2.5 flex items-center gap-2" data-testid={`sar-in-project-${p.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-[9px] font-black tracking-widest text-[#A3FF12]">INFORME DE ADQUISICIÓN</div>
                            <div className="text-white text-xs font-bold">Generar SAR desde este proyecto</div>
                          </div>
                          <SARButton
                            lat={siteInfo.lat} lng={siteInfo.lng}
                            address={siteInfo.address}
                            height_m={siteInfo.height_m || null}
                            site_id={siteInfo.id || siteInfo.code || null}
                            site_name={`${siteInfo.name || siteInfo.code || ''} · ${p.name}`}
                            afecciones_report={siteInfo.afecciones_report || null}
                            variant="primary"/>
                        </div>
                      )}
                      {canWrite && (
                        <div className="flex gap-2 flex-wrap text-[11px]">
                          <button onClick={() => setCreatingFolderIn('root')}
                            className="inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-2 py-1 rounded-lg font-bold">
                            <FolderPlus size={12}/> Nueva carpeta
                          </button>
                          <button onClick={() => triggerUpload(p.id, null)}
                            className="inline-flex items-center gap-1 bg-[#A3FF12]/10 hover:bg-[#A3FF12]/20 border border-[#A3FF12]/30 text-[#A3FF12] px-2 py-1 rounded-lg font-bold"
                            data-testid={`btn-upload-${p.id}`}>
                            <FilePlus size={12}/> Subir archivo
                          </button>
                        </div>
                      )}
                      {creatingFolderIn === 'root' && (
                        <div className="flex gap-2">
                          <input value={newFolderName} autoFocus onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder="Nombre de carpeta"
                            onKeyDown={(e) => e.key === 'Enter' && createFolder(p.id, null)}
                            className="flex-1 bg-black border border-white/10 rounded px-2 py-1 text-xs text-white outline-none"/>
                          <button onClick={() => createFolder(p.id, null)} className="text-[#A3FF12]"><Check size={14}/></button>
                          <button onClick={() => { setCreatingFolderIn(null); setNewFolderName(''); }} className="text-white/40"><X size={14}/></button>
                        </div>
                      )}
                      {/* Root-level docs (no folder) */}
                      {tree.documents.filter((d) => !d.folder_id).map((doc) => (
                        <DocRow key={doc.id} doc={doc} canWrite={canWrite}
                          onDownload={() => downloadDoc(doc)}
                          onDelete={() => deleteDoc(p.id, doc.id)}/>
                      ))}
                      {/* Folders */}
                      {tree.folders.map((f) => (
                        <FolderBlock key={f.id} folder={f}
                          docs={tree.documents.filter((d) => d.folder_id === f.id)}
                          canWrite={canWrite}
                          onUpload={() => triggerUpload(p.id, f.id)}
                          onDelete={() => deleteFolder(p.id, f.id)}
                          onDownloadDoc={downloadDoc}
                          onDeleteDoc={(docId) => deleteDoc(p.id, docId)}/>
                      ))}
                      {tree.folders.length === 0 && tree.documents.length === 0 && (
                        <div className="text-center text-white/40 text-xs py-3">
                          Proyecto vacío. {canWrite && 'Sube el primer archivo o crea una carpeta.'}
                        </div>
                      )}

                      {/* Botón para desplegar timeline / seguimiento */}
                      <button onClick={() => setTimelineOpen((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                        className="text-[10px] font-bold uppercase inline-flex items-center gap-1 text-white/50 hover:text-white border-t border-white/5 w-full justify-center py-1.5 mt-1"
                        data-testid={`btn-timeline-${p.id}`}>
                        <History size={11}/> {timelineOpen[p.id] ? 'Ocultar' : 'Ver'} seguimiento
                      </button>
                      {timelineOpen[p.id] && (
                        <ProjectTimeline projectId={p.id} onClose={() => setTimelineOpen((prev) => ({ ...prev, [p.id]: false }))}/>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DocRow = ({ doc, canWrite, onDownload, onDelete }) => (
  <div className="flex items-center gap-2 bg-black/30 hover:bg-black/50 border border-white/5 rounded-lg px-2.5 py-1.5 group" data-testid={`doc-${doc.id}`}>
    <FileText size={13} className="text-white/40 flex-shrink-0"/>
    <span className="text-xs text-white truncate flex-1">{doc.name}</span>
    <span className="text-[10px] text-white/40 flex-shrink-0">{humanSize(doc.size_bytes)}</span>
    <button onClick={onDownload} className="text-white/40 hover:text-[#A3FF12] p-1" title="Descargar"><Download size={13}/></button>
    {canWrite && <button onClick={onDelete} className="text-white/40 hover:text-red-400 p-1" title="Eliminar"><Trash2 size={13}/></button>}
  </div>
);

const FolderBlock = ({ folder, docs, canWrite, onUpload, onDelete, onDownloadDoc, onDeleteDoc }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-black/20 border border-white/5 rounded-lg" data-testid={`folder-${folder.id}`}>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button onClick={() => setOpen(!open)} className="text-white/50 hover:text-white">
          {open ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
        </button>
        <Folder size={13} className="text-[#A3FF12]"/>
        <span className="text-xs text-white font-bold flex-1 truncate">{folder.name}</span>
        <span className="text-[10px] text-white/40">{docs.length} archivo{docs.length === 1 ? '' : 's'}</span>
        {canWrite && (
          <>
            <button onClick={onUpload} className="text-white/40 hover:text-[#A3FF12] p-1" title="Subir aquí"><FilePlus size={12}/></button>
            <button onClick={onDelete} className="text-white/40 hover:text-red-400 p-1" title="Eliminar carpeta"><Trash2 size={12}/></button>
          </>
        )}
      </div>
      {open && (
        <div className="pl-6 pr-2 pb-2 space-y-1">
          {docs.length === 0 ? (
            <div className="text-[10px] text-white/30 italic pl-1">Sin documentos</div>
          ) : docs.map((d) => (
            <DocRow key={d.id} doc={d} canWrite={canWrite}
              onDownload={() => onDownloadDoc(d)}
              onDelete={() => onDeleteDoc(d.id)}/>
          ))}
        </div>
      )}
    </div>
  );
};

export default SiteProjectsPanel;
